import CommonIriDisplay from '../application-core/CommonIriDisplay';
import DescriptionIcon from '@material-ui/icons/Description';
import { Typography } from '@material-ui/core';
import LanguageIcon from '@material-ui/icons/Language';
import LinkIcon from '@material-ui/icons/Link';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React from 'react';
import BasicLanguageField from '../core/BasicLanguageField';
import TitledPanel from '../core/TitledPanel';
import TypedLiteralDialog from '../core/TypedLiteralDialog';
import FindResourceDialog from '../ontology/FindResourceDialog';
import BooleanField from './BooleanField';
import DateField from './DateField';
import DatetimeField from './DatetimeField';
import GetBooleanValueDialog from './GetBooleanValueDialog';
import GetDatatypeValueDialog from './GetDatatypeValueDialog';
import GetDatetimeValueDialog from './GetDatetimeValueDialog';
import GetDateValueDialog from './GetDateValueDialog';
import LangConfirmationField from './LangConfirmationField';
import LiteralConfirmationField from './LiteralConfirmationField';
import Notification from './Notification';
import { DATATYPES, MEDIA_TYPES, MEDIA_TYPES_CONTENT } from './utils/constants';
import { computeDatatype, sortResources } from './utils/utils';

const TREAT_MAX_LENGTH = 10000;

class DatatypeDisplay extends React.Component {

  render() {

    const { isReadOnly, title, datatype, values, onAddLiteral, onAddTypedLiteral, defaultLang, projectLang, deleted, deprecated, info, graph,
      onDelete, onUpdate, property, source, associatedProjectGraph, viewLang, addNewResource, context, noRangeOptions, sourceComponent,
      canAddValues, cardinalityAtMax, exceedsCardinality, searchClasses, information, projectIRI, useWorkflow,
      components, applicableStatements, reify, onReify, exclude } = this.props;

    let dt = datatype;
    if (dt === "http://www.w3.org/2000/01/rdf-schema#Literal") dt = DATATYPES.STRING;

    let indexedValues = cloneDeep(values).map((value, index) => ({
      index,
      data: value
    }));

    // Although this is a datatype component we are flexible enough to handle no range so we process IRIs too
    let existingResourceValues = values.filter(value => value.id);
    // We add in disjoint values too
    let existingResourceIRIs = [...existingResourceValues.map(value => value.id), ...exclude];
    const fullExclusionList = [...values, ...exclude];

    // We need the existing langstring values in a form we can feed into BasicLanguageField
    let existingLangstringValues = {};
    fullExclusionList
      .filter(value => value.language)
      .forEach(value => Array.isArray(existingLangstringValues[value.language]) ?
        existingLangstringValues[value.language].push(value["@value"])
        :
        existingLangstringValues[value.language] = [value["@value"]]
      );

    // For other values we simply extract a value
    const existingValues = fullExclusionList
      .filter(value => !value.language && !value.id)
      .map(value => value["@value"] || value);

    // Separate to the ordered indexed values we need datatypes for typed literals
    // We exclude booleans as done separately (will have true/false values)
    // This code is also in DatatypeHandler
    const existingTypedLiteralValues = fullExclusionList
      .filter(value => !value.language && !value.id && !(typeof value === "boolean"))
      .map(content => {
        const dt = computeDatatype(content);
        const tc = typeof content === "object";
        const text = tc ?
          content["@value"]
          :
          content;
        return {
          text,
          datatype: tc ?
            content["type"]
            :
            dt || DATATYPES.STRING
        };
      });

    // We need to extract out non langstring fields
    const orderedValues = indexedValues
      .sort((a, b) => {
        const aValue = a.data["@value"] || a.data;
        const bValue = b.data["@value"] || b.data;
        if (aValue.toString().toLowerCase() < bValue.toString().toLowerCase()) return -1;
        if (aValue.toString().toLowerCase() > bValue.toString().toLowerCase()) return 1;
        const aType = a.data.type || DATATYPES.STRING;
        const bType = b.data.type || DATATYPES.STRING;
        if (aType < bType) return -1;
        if (aType > bType) return 1;
        return 0;
      });

    let dialog = null;
    let knownDatatype = true;
    const isMultiline = [MEDIA_TYPES.MARKDOWN, MEDIA_TYPES.MATHML, MEDIA_TYPES.SVG, MEDIA_TYPES.HTML, MEDIA_TYPES.MULTILINE].includes(info.treatContentAs);
    const maxLength = info.maxLength ?
      info.maxLength
      :
      isMultiline ?
        TREAT_MAX_LENGTH
        :
        1000;

    if (!isReadOnly && !info.patternConflict && !info.treatContentAsConflict && !cardinalityAtMax && canAddValues
      && !deleted && !deprecated && !(dt === DATATYPES.BOOLEAN && values.length > 0)) {
      switch (dt) {
        case "":
          // If no range then we give the user the option to select from a resource or one of our supported datatypes
          dialog = [
            <FindResourceDialog
              key={viewLang}
              source={source}
              defaultLang={defaultLang}
              components={components}
              useWorkflow={useWorkflow}
              searchClass={searchClasses}
              includeSystemGraph={false}
              sourceComponent={sourceComponent}
              buttonTitle="Add a resource"
              title="Add Resource"
              permitExternal={true}
              graph={graph}
              icon={<LinkIcon />}
              viewLang={viewLang}
              exclude={existingResourceIRIs || []}
              onAddResource={addNewResource} />
          ];
          if (noRangeOptions.boolean) {
            dialog.push(<GetBooleanValueDialog
              key="boolean-dialog"
              disabled={(existingValues && existingValues.length > 0)
                || (existingResourceValues && existingResourceValues.length > 0)
                || (exclude.length > 0)}
              icon={<ToggleOnIcon />}
              name={title}
              onAddLiteral={onAddTypedLiteral} />);
          }
          if (noRangeOptions.language) {
            dialog.push(<BasicLanguageField
              key="langstring-dialog"
              dataTest="langstring"
              icon={<LanguageIcon />}
              enforceLanguage={true}
              existingValues={existingLangstringValues}
              multiline={false}
              maxLength={maxLength}
              defaultLang={defaultLang}
              langs={projectLang}
              title={"Add Value"}
              onAddLanguage={onAddLiteral} />);
          }
          if (noRangeOptions.dateTime) {
            dialog.push(<GetDatetimeValueDialog
              key="datetime-dialog"
              title={"Add " + title}
              icon={<QueryBuilderIcon />}
              name={title}
              existingValues={existingValues}
              onSetDate={onAddTypedLiteral} />);
          }
          if (noRangeOptions.datatype) {
            dialog.push(<TypedLiteralDialog
              key="literal-dialog"
              dataTest="add-typed-literal"
              onAddLiteral={onAddTypedLiteral}
              existingValues={existingTypedLiteralValues}
              excludeDatatypes={noRangeOptions.excludeDatatypes}
              title="Add Value"
              placeholder="Enter text"
              name={title} />);
          }
          break;
        case DATATYPES.LANGSTRING:
          dialog = <BasicLanguageField
            dataTest="langstring"
            existingValues={existingLangstringValues}
            multiline={isMultiline}
            excludeNoLanguage={true}
            maxLength={maxLength}
            defaultLang={defaultLang}
            langs={projectLang}
            title={"Add " + title}
            onAddLanguage={onAddLiteral} />;
          break;
        case DATATYPES.DATE:
          dialog = <GetDateValueDialog
            title={"Add " + title}
            name={title}
            existingValues={existingValues}
            onSetDate={onAddTypedLiteral} />;
          break;
        case DATATYPES.DATETIME:
          dialog = <GetDatetimeValueDialog
            title={"Add " + title}
            name={title}
            existingValues={existingValues}
            onSetDate={onAddTypedLiteral} />;
          break;
        case DATATYPES.BOOLEAN:
          dialog = <GetBooleanValueDialog name={title} onAddLiteral={onAddTypedLiteral} />;
          break;
        case DATATYPES.STRING: case DATATYPES.ANYURI:
          dialog = <GetDatatypeValueDialog
            title={"Add " + title}
            maxLength={maxLength}
            pattern={info.pattern}
            name={title}
            placeholder="Enter text"
            showDatatype={!info.treatContentAs}
            treatContentAs={info.treatContentAs}
            validationDatatype={
              info.treatContentAs === MEDIA_TYPES.IMAGES ?
                "http://www.w3.org/2001/XMLSchema#anyURI"
                :
                dt
            }
            datatype={dt}
            existingValues={existingValues}
            onAddLiteral={onAddTypedLiteral} />;
          break;
        case DATATYPES.INTEGER: case DATATYPES.NNINTEGER: case DATATYPES.DECIMAL: case DATATYPES.FLOAT: case DATATYPES.DOUBLE: case DATATYPES.LONG:
          dialog = <GetDatatypeValueDialog
            title={"Add " + title}
            name={title}
            datatype={dt}
            placeholder="Enter text"
            existingValues={existingValues}
            onAddLiteral={onAddTypedLiteral} />;
          break;
        default:
          dialog = null;
          knownDatatype = false;
      }
    }

    let existingResourceValuesIDs = existingResourceValues.map(value => value.id);
    let sortedExistingResourceValues = sortResources(existingResourceValuesIDs, graph, "label", defaultLang);
    let resourceFields = sortedExistingResourceValues.map(id => {
      return <CommonIriDisplay
        key={id}
        id={id}
        projectIRI={projectIRI}
        loadComponents={true}
        context={context}
        onDeleteIRI={() => onDelete(property, null, null, graph)(id)}
        handleNotAvailable={true}
        isReadOnly={isReadOnly}
        sourceGraph={graph}
        wrap={false}
        graph={graph}
        property={property}
        source={source}
        viewLang={defaultLang}
        defaultLang={defaultLang} />;
    });

    // First we display any langstring fields
    let langstringFields = null;
    langstringFields = orderedValues
      .filter(value => value.data.language)
      .sort((a, b) => {
        if (a.data.language < b.data.language) return -1;
        if (a.data.language > b.data.language) return 1;
        if (a.data["@value"].toLowerCase() < b.data["@value"].toLowerCase()) return -1;
        if (a.data["@value"].toLowerCase() > b.data["@value"].toLowerCase()) return 1;
        return 0;
      })
      .map((value) => {
        const lang = value.data.language;
        return <LangConfirmationField
          dataTest="langstring"
          maxLength={maxLength}
          key={lang + "-" + value.data["@value"]}
          onUpdate={onUpdate ? onUpdate(property, lang, value.index, associatedProjectGraph) : null}
          onDelete={onDelete ? onDelete(property, lang, null, associatedProjectGraph) : null}
          existingValues={fullExclusionList
            .filter(existingValue => existingValue.language === lang)
            .map(existingValue => existingValue["@value"] || existingValue)}
          canBeDeleted={onDelete ? true : false}
          invalidDatatype={dt !== DATATYPES.LANGSTRING && dt !== "" && datatype !== "http://www.w3.org/2000/01/rdf-schema#Literal"}
          text={value.data["@value"]}
          lang={lang}
          multiline={isMultiline}
          graph={graph}
          reify={reify}
          onReify={onReify}
          applicableStatements={applicableStatements}
          source={source}
          property={property}
          isReadOnly={isReadOnly} />;
      });

    // When sorting we need to be careful to retain the original index position in the array for updates
    return <TitledPanel
      warning={
        exceedsCardinality ?
          "Too many values. Only " + (info.maxCount || 1) + " expected."
          :
          ""}
      dataTest={title ? "property-" + title : "property"}
      height="full"
      actions={dialog}
      information={information}
      title={info.name || title}>
      {info.treatContentAs && info.treatContentAs !== "text/plain" &&
        <Typography paragraph data-test="pattern-expected" variant="caption">This property is expecting text of the form: {MEDIA_TYPES_CONTENT[info.treatContentAs]}</Typography>
      }
      {info.description &&
        <div style={{ marginBottom: 12 }}>
          <Typography variant="body2"><DescriptionIcon style={{ transform: 'translateY(4px)' }} fontSize="small" /> <span data-test="description">{info.description}</span></Typography>
        </div>
      }
      {info.minCount && orderedValues.length < info.minCount && <Notification dataTest="warning" error={false} text={"Min. values required: " + info.minCount} />}
      {info.treatContentAsConflict && <Notification dataTest="treatcontentas-conflict" error={false} text={"Treat content as from multiple templates are conflicting. Please fix."} />}
      {info.patternConflict && <Notification dataTest="pattern-conflict" error={false} text={"Patterns from multiple templates are conflicting. Please fix."} />}
      {info.minCountConflict && <Notification dataTest="min-count-conflict" error={false} text={"Min. Count values from multiple templates are conflicting. The largest value is being used."} />}
      {info.maxCountConflict && <Notification dataTest="max-count-conflict" error={false} text={"Max. Count values from multiple templates are conflicting. The smallest value is being used."} />}
      {info.maxLengthConflict && <Notification dataTest="max-length-conflict" error={false} text={"Max. Length values from multiple templates are conflicting. The smallest value is being used."} />}
      {!knownDatatype && <Notification error={false} text="This property has an unsupported range." dataTest="unsupported" />}
      {deleted && <Notification error={true} text="This property is pending deletion" dataTest="pending-deletion" />}
      {deprecated && <Notification error={false} text="This property is deprecated." dataTest="deprecated" />}
      {canAddValues === false &&
        <Notification error={false} text="This resource no longer has a class valid for this property." dataTest="not-valid" />
      }
      {resourceFields}
      {langstringFields}
      {orderedValues &&
        orderedValues
          .filter(value => !value.data.language && !value.data.id)
          .map(value => {
            // We need to consider the fact that the range of the property may conflict with the actual value
            // We display the value datatype as is but show warning
            let typeToCheck = dt;
            let valueText = value.data["@value"] || value.data;
            if (value.data.type) {
              typeToCheck = value.data.type;
            }
            else {
              if (typeof valueText === "boolean" && dt !== DATATYPES.BOOLEAN) typeToCheck = DATATYPES.BOOLEAN;
              else if (typeof valueText === "number") typeToCheck = DATATYPES.INTEGER;
              else if (typeof valueText === "string" && dt !== DATATYPES.STRING) typeToCheck = DATATYPES.STRING;
            }
            switch (typeToCheck) {
              case DATATYPES.DATE:
                return <DateField
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  key={valueText}
                  isReadOnly={isReadOnly}
                  date={valueText}
                  graph={graph}
                  reify={reify}
                  onReify={onReify}
                  applicableStatements={applicableStatements}
                  source={source}
                  property={property}
                  existingValues={existingValues}
                  onDelete={onDelete ? onDelete(property, null, typeToCheck, associatedProjectGraph) : null}
                  onUpdate={onUpdate ? onUpdate(property, null, value.index, associatedProjectGraph) : null} />;
              case DATATYPES.DATETIME:
                return <DatetimeField
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  key={valueText}
                  isReadOnly={isReadOnly}
                  dateTime={valueText}
                  graph={graph}
                  reify={reify}
                  onReify={onReify}
                  applicableStatements={applicableStatements}
                  source={source}
                  property={property}
                  existingValues={existingValues}
                  onDelete={onDelete ? onDelete(property, null, typeToCheck, associatedProjectGraph) : null}
                  onUpdate={onUpdate ? onUpdate(property, null, value.index, associatedProjectGraph) : null} />;
              case DATATYPES.BOOLEAN:
                return <BooleanField
                  key={"boolean-" + valueText}
                  graph={graph}
                  reify={reify}
                  onReify={onReify}
                  applicableStatements={applicableStatements}
                  source={source}
                  property={property}
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  isReadOnly={isReadOnly}
                  checked={typeof valueText === "boolean" ? valueText : valueText === "true"}
                  onDelete={onDelete ? onDelete(property, null, typeToCheck, associatedProjectGraph) : null}
                  onUpdate={onUpdate ? onUpdate(property, null, value.index, associatedProjectGraph) : null} />;
              case DATATYPES.STRING: case DATATYPES.ANYURI:
                return <LiteralConfirmationField
                  dataTest={title || "datatype"}
                  key={typeToCheck + "-" + valueText}
                  graph={graph}
                  reify={reify}
                  onReify={onReify}
                  applicableStatements={applicableStatements}
                  source={source}
                  ariaLabel={"Enter "+ title}
                  property={property}
                  margin="dense"
                  text={valueText}
                  maxLength={maxLength}
                  pattern={info.pattern}
                  treatContentAs={info.treatContentAs}
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  datatype={typeToCheck}
                  validationDatatype={
                    info.treatContentAs === MEDIA_TYPES.IMAGES ?
                      "http://www.w3.org/2001/XMLSchema#anyURI"
                      :
                      dt
                  }
                  existingValues={existingTypedLiteralValues}
                  canBeDeleted={onDelete ? true : false}
                  onDelete={onDelete ? onDelete(property, null, typeToCheck, associatedProjectGraph) : null}
                  onUpdate={onUpdate ? onUpdate(property, null, value.index, associatedProjectGraph) : null}
                  isReadOnly={isReadOnly} />;
              case DATATYPES.INTEGER: case DATATYPES.NNINTEGER: case DATATYPES.DECIMAL: case DATATYPES.FLOAT: case DATATYPES.DOUBLE: case DATATYPES.LONG:
                return <LiteralConfirmationField
                  dataTest={title || "datatype"}
                  key={typeToCheck + "-" + valueText}
                  graph={graph}
                  reify={reify}
                  onReify={onReify}
                  ariaLabel={"Enter "+ title}
                  applicableStatements={applicableStatements}
                  source={source}
                  property={property}
                  margin="dense"
                  text={valueText.toString()}
                  datatype={typeToCheck}
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  existingValues={existingTypedLiteralValues}
                  canBeDeleted={onDelete ? true : false}
                  onDelete={onDelete ? onDelete(property, null, typeToCheck, associatedProjectGraph) : null}
                  onUpdate={onUpdate ? onUpdate(property, null, value.index, associatedProjectGraph) : null}
                  isReadOnly={isReadOnly}
                />;
              default: return null;
            }
          })}
    </TitledPanel>;
  }
}

DatatypeDisplay.defaultProps = {
  datatype: DATATYPES.STRING,
  cardinalityAtMax: false,
  canAddValues: undefined,
  info: {},
  exclude: [],
  searchClasses: [],
  noRangeOptions: {
    boolean: true,
    language: true,
    dateTime: true,
    datatype: true,
    reify: false,
    excludeDatatypes: []
  }
};

DatatypeDisplay.propTypes = {
  canAddValues: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  deprecated: PropTypes.bool,
  deleted: PropTypes.bool,
  source: PropTypes.string,
  info: PropTypes.object,
  context: PropTypes.string,
  cardinalityAtMax: PropTypes.bool,
  exceedsCardinality: PropTypes.bool,
  datatype: PropTypes.string,
  components: PropTypes.array,
  onAddLiteral: PropTypes.func,
  reify: PropTypes.bool,
  onReify: PropTypes.func,
  projectIRI: PropTypes.string,
  noRangeOptions: PropTypes.object,
  onAddTypedLiteral: PropTypes.func,
  title: PropTypes.string.isRequired,
  sourceComponent: PropTypes.string,
  viewLang: PropTypes.string,
  searchClasses: PropTypes.array,
  exclude: PropTypes.array,
  projectLang: PropTypes.array.isRequired,
  graph: PropTypes.string,
  defaultLang: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
  useWorkflow: PropTypes.bool,
  associatedProjectGraph: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onUpdate: PropTypes.func,
  information: PropTypes.object,
  values: PropTypes.array.isRequired,
  addNewResoure: PropTypes.func
};

export default DatatypeDisplay;